import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { logError, logBreadcrumb } from '../utils/logger';

export const useDataSync = (userSettings, setUserSettings, progress, setProgress, checkAchievementProgress) => {
    const { user } = useAuth();
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSynced, setLastSynced] = useState(null);

    // Load data from cloud on login
    useEffect(() => {
        if (!user) return;

        const loadCloudData = async () => {
            setIsSyncing(true);
            try {
                // 0. Fetch Profile (username, callsign, country, region, stats, streak freezes)
                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('username, call_sign, country, region_code, streak, streak_freezes, streak_freeze_last_earned, streak_freeze_used_date, max_wpm, lifetime_score, achievements, unlocked_trivia, skill_level, skill_level_updated_at, skill_assessment_completed, is_moderated')
                    .eq('id', user.id)
                    .single();

                // 1. Fetch Settings
                const { data: settingsData, error: settingsError } = await supabase
                    .from('user_settings')
                    .select('settings')
                    .eq('user_id', user.id)
                    .maybeSingle();

                // Merge profile data into settings
                const cloudSettings = settingsData?.settings || {};
                if (profileData) {
                    cloudSettings.username = profileData.username || cloudSettings.username || '';
                    cloudSettings.callSign = profileData.call_sign || cloudSettings.callSign || '';
                    cloudSettings.country = profileData.country || cloudSettings.country || 'US';
                    cloudSettings.regionCode = profileData.region_code || cloudSettings.regionCode || '';

                    // Leaderboard stats from profiles take precedence if present
                    if (profileData.streak !== undefined && profileData.streak !== null) cloudSettings.streak = profileData.streak;
                    if (profileData.streak_freezes !== undefined && profileData.streak_freezes !== null) cloudSettings.streakFreezes = profileData.streak_freezes;
                    if (profileData.streak_freeze_last_earned) cloudSettings.streakFreezeLastEarned = profileData.streak_freeze_last_earned;
                    if (profileData.streak_freeze_used_date) cloudSettings.streakFreezeUsedDate = profileData.streak_freeze_used_date;
                    if (profileData.max_wpm) cloudSettings.maxWpm = profileData.max_wpm;
                    if (profileData.lifetime_score) cloudSettings.lifetimeScore = profileData.lifetime_score;
                    if (profileData.achievements) cloudSettings.achievements = profileData.achievements;
                    if (profileData.unlocked_trivia) cloudSettings.unlockedTrivia = profileData.unlocked_trivia;
                    if (profileData.skill_level) cloudSettings.skill_level = profileData.skill_level;
                    if (profileData.skill_level_updated_at) cloudSettings.skill_level_updated_at = profileData.skill_level_updated_at;
                    if (profileData.skill_assessment_completed !== null) cloudSettings.skill_assessment_completed = profileData.skill_assessment_completed;
                }

                if (Object.keys(cloudSettings).length > 0) {
                    console.log('Loaded settings from cloud:', cloudSettings);
                    if (setUserSettings) {
                        setUserSettings(prev => {
                            const merged = { ...prev, ...cloudSettings };

                            // Smart Merge: Lifetime Score
                            // If the account has been moderated, we trust the cloud value even if it is lower
                            const localScore = Number(prev.lifetimeScore || 0);
                            const cloudScore = Number(cloudSettings.lifetimeScore || 0);

                            // Check for large discrepancy (potential data loss indicator)
                            const scoreDiff = Math.abs(localScore - cloudScore);
                            const largerScore = Math.max(localScore, cloudScore);
                            const discrepancyThreshold = 5000; // Alert if difference > 5000 XP
                            const percentThreshold = 0.2; // Or if difference > 20% of larger value

                            if (scoreDiff > discrepancyThreshold || (largerScore > 0 && scoreDiff / largerScore > percentThreshold)) {
                                console.warn(`[XP Sync] Large discrepancy detected: local=${localScore}, cloud=${cloudScore}, diff=${scoreDiff}`);
                                logBreadcrumb('XP large discrepancy', 'warning', {
                                    local: localScore,
                                    cloud: cloudScore,
                                    difference: scoreDiff,
                                    percentDiff: largerScore > 0 ? ((scoreDiff / largerScore) * 100).toFixed(1) + '%' : 'N/A'
                                });
                            }

                            if (profileData?.is_moderated) {
                                merged.lifetimeScore = cloudScore;
                                console.log(`[XP Sync] Moderated account - using cloud value: ${cloudScore}`);
                            } else {
                                merged.lifetimeScore = Math.max(localScore, cloudScore);
                                // Log when there's a difference to help debug sync issues
                                if (localScore !== cloudScore) {
                                    console.log(`[XP Sync] Merge: local=${localScore}, cloud=${cloudScore}, result=${merged.lifetimeScore}`);
                                    logBreadcrumb('XP sync merge', 'data', {
                                        local: localScore,
                                        cloud: cloudScore,
                                        result: merged.lifetimeScore,
                                        source: localScore > cloudScore ? 'local' : 'cloud'
                                    });
                                }
                            }

                            // Smart Merge: Max WPM
                            if (profileData?.is_moderated) {
                                merged.maxWpm = cloudSettings.maxWpm || 0;
                            } else {
                                merged.maxWpm = Math.max(prev.maxWpm || 0, cloudSettings.maxWpm || 0);
                            }

                            // Smart Merge: WPM History
                            const localHistory = prev.wpmHistory || [];
                            const cloudHistory = cloudSettings.wpmHistory || [];
                            const historyMap = new Map();

                            if (profileData?.is_moderated) {
                                // For moderated accounts, we only trust the cloud history
                                cloudHistory.forEach(h => historyMap.set(h.date, h));
                            } else {
                                // Add local history first
                                localHistory.forEach(h => historyMap.set(h.date, h));

                                // Merge cloud history, keeping higher WPM for same date
                                cloudHistory.forEach(h => {
                                    if (historyMap.has(h.date)) {
                                        const existing = historyMap.get(h.date);
                                        if (h.wpm > existing.wpm) {
                                            historyMap.set(h.date, h);
                                        }
                                    } else {
                                        historyMap.set(h.date, h);
                                    }
                                });
                            }

                            merged.wpmHistory = Array.from(historyMap.values()).sort((a, b) => a.date.localeCompare(b.date));

                            // Smart Merge: Unlocked Trivia
                            if (profileData?.is_moderated) {
                                merged.unlockedTrivia = cloudSettings.unlockedTrivia || [];
                            } else {
                                const localTrivia = new Set(prev.unlockedTrivia || []);
                                (cloudSettings.unlockedTrivia || []).forEach(t => localTrivia.add(t));
                                merged.unlockedTrivia = Array.from(localTrivia);
                            }

                            // Smart Merge: Achievements
                            if (profileData?.is_moderated) {
                                merged.achievements = cloudSettings.achievements || [];
                            } else {
                                const localAchievements = new Set(prev.achievements || []);
                                (cloudSettings.achievements || []).forEach(a => localAchievements.add(a));
                                merged.achievements = Array.from(localAchievements);
                            }

                            // Smart Merge: Modes History
                            const localModes = new Set(prev.modesHistory || []);
                            (cloudSettings.modesHistory || []).forEach(m => localModes.add(m));
                            merged.modesHistory = Array.from(localModes);

                            // Persist merged state to local storage
                            localStorage.setItem('morse_settings_v1', JSON.stringify(merged));
                            return merged;
                        });
                    }
                }

                // 2. Fetch Progress
                const { data: progressData, error: progressError } = await supabase
                    .from('user_progress')
                    .select('progress')
                    .eq('user_id', user.id)
                    .single();

                if (progressData?.progress) {
                    console.log('Loaded progress from cloud:', progressData.progress);
                    if (setProgress) {
                        setProgress(prev => {
                            const cloudProgress = progressData.progress;
                            const merged = { ...prev, ...cloudProgress };

                            // Smart Merge: Level
                            if (profileData?.is_moderated) {
                                merged.level = cloudProgress.level || 1;
                                merged.maxLevel = cloudProgress.maxLevel || 1;
                            } else {
                                merged.level = Math.max(prev.level || 1, cloudProgress.level || 1);
                                merged.maxLevel = Math.max(prev.maxLevel || 1, cloudProgress.maxLevel || 1);
                            }

                            // If local level is higher, keep local history. Otherwise take cloud history.
                            if ((prev.level || 1) > (cloudProgress.level || 1)) {
                                merged.history = prev.history;
                            } else if ((cloudProgress.level || 1) > (prev.level || 1)) {
                                merged.history = cloudProgress.history;
                            }

                            localStorage.setItem('morse_progress_v2', JSON.stringify(merged));
                            return merged;
                        });
                    }
                }

                // 3. Fetch Literature Progress
                const { data: litData, error: litError } = await supabase
                    .from('literature_progress')
                    .select('*')
                    .eq('user_id', user.id);

                if (litData && litData.length > 0) {
                    console.log('Loaded literature progress from cloud:', litData.length);
                    if (setUserSettings) {
                        setUserSettings(prev => {
                            const localHistory = prev.literatureHistory || {};
                            const mergedHistory = { ...localHistory };

                            litData.forEach(item => {
                                const local = localHistory[item.literature_id];
                                if (!local || (new Date(item.last_accessed) > new Date(local.lastAccessed))) {
                                    mergedHistory[item.literature_id] = {
                                        id: item.literature_id,
                                        status: item.status,
                                        completedSegments: item.completed_segments,
                                        lastAccessed: item.last_accessed,
                                        bestWpm: item.best_wpm,
                                        bestAccuracy: item.best_accuracy
                                    };
                                }
                            });

                            const updated = { ...prev, literatureHistory: mergedHistory };
                            localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
                            return updated;
                        });
                    }
                }

                // 4. Fetch Achievements
                const { data: achievementData, error: achievementError } = await supabase
                    .from('user_achievements')
                    .select('achievement_id')
                    .eq('user_id', user.id);

                if (achievementData && achievementData.length > 0) {
                    console.log('Loaded achievements from cloud:', achievementData.length);
                    if (setUserSettings) {
                        setUserSettings(prev => {
                            const localAchievs = new Set(prev.achievements || []);
                            achievementData.forEach(a => localAchievs.add(a.achievement_id));
                            const updated = { ...prev, achievements: Array.from(localAchievs) };
                            localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
                            return updated;
                        });
                    }
                }

                // 5. Fetch Unlocked Trivia
                const { data: triviaData, error: triviaError } = await supabase
                    .from('user_trivia_unlocks')
                    .select('trivia_id')
                    .eq('user_id', user.id);

                if (triviaData && triviaData.length > 0) {
                    console.log('Loaded trivia from cloud:', triviaData.length);
                    if (setUserSettings) {
                        setUserSettings(prev => {
                            const localTrivia = new Set(prev.unlockedTrivia || []);
                            triviaData.forEach(t => localTrivia.add(t.trivia_id));
                            const updated = { ...prev, unlockedTrivia: Array.from(localTrivia) };
                            localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
                            return updated;
                        });
                    }
                }

                setLastSynced(new Date());
            } catch (err) {
                console.error('Error loading cloud data:', err);
            } finally {
                setIsSyncing(false);
            }
        };

        if (setUserSettings || setProgress) {
            loadCloudData();
        }
    }, [user]); // Only run when user changes (logs in)

    // Save data to cloud
    const saveToCloud = useCallback(async (newSettings, newProgress) => {
        if (!user) return;

        // Debounce or just fire? For now, we'll rely on the caller to not spam this.
        // In a real app, we might want a queue or debounce.

        try {
            if (newSettings) {
                // PROTECTION: Fetch current cloud values to prevent accidental overwrites
                const { data: currentProfile } = await supabase
                    .from('profiles')
                    .select('lifetime_score, max_wpm, is_moderated')
                    .eq('id', user.id)
                    .single();

                const localScore = Number(newSettings.lifetimeScore || 0);
                const cloudScore = Number(currentProfile?.lifetime_score || 0);
                const isModerated = currentProfile?.is_moderated || false;

                // Determine safe score to sync (always use higher unless moderated)
                let safeScore = localScore;
                let needsLocalFix = false;
                if (!isModerated && cloudScore > localScore) {
                    console.warn(`[XP Protection] Prevented score decrease: local=${localScore}, cloud=${cloudScore}. Using cloud value.`);
                    logBreadcrumb('XP protection triggered', 'warning', {
                        local: localScore,
                        cloud: cloudScore,
                        action: 'prevented_decrease'
                    });
                    safeScore = cloudScore;
                    needsLocalFix = true;
                } else if (localScore > cloudScore) {
                    console.log(`[XP Sync] Score increase: ${cloudScore} -> ${localScore} (+${localScore - cloudScore})`);
                }

                // Same protection for max WPM
                const localWpm = Number(newSettings.maxWpm || 0);
                const cloudWpm = Number(currentProfile?.max_wpm || 0);
                const safeWpm = isModerated ? localWpm : Math.max(localWpm, cloudWpm);
                if (!isModerated && cloudWpm > localWpm) {
                    needsLocalFix = true;
                }

                // If cloud had higher values, fix local state to prevent repeated protection triggers
                if (needsLocalFix && setUserSettings) {
                    setUserSettings(prev => {
                        const fixed = { ...prev, lifetimeScore: safeScore, maxWpm: safeWpm };
                        localStorage.setItem('morse_settings_v1', JSON.stringify(fixed));
                        console.log('[XP Protection] Local state corrected to match cloud');
                        return fixed;
                    });
                }

                // 1. Save to user_settings (JSON blob)
                // Sanitize settings to remove PII or derived state before saving
                const { email, ...sanitizedSettings } = newSettings;
                // Update settings with safe values
                sanitizedSettings.lifetimeScore = safeScore;
                sanitizedSettings.maxWpm = safeWpm;

                const { error: settingsError } = await supabase
                    .from('user_settings')
                    .upsert({ user_id: user.id, settings: sanitizedSettings, updated_at: new Date() });
                if (settingsError) throw settingsError;

                // 2. Update profiles table (Structured data for leaderboards/search)
                const { error: profileError } = await supabase
                    .from('profiles')
                    .update({
                        username: newSettings.username,
                        call_sign: newSettings.callSign,
                        country: newSettings.country,
                        region_code: newSettings.regionCode,
                        streak: newSettings.streak || 0,
                        max_wpm: safeWpm,
                        lifetime_score: safeScore,
                        achievements: newSettings.achievements || [],
                        unlocked_trivia: newSettings.unlockedTrivia || [],
                        skill_level: newSettings.skill_level || null,
                        skill_level_updated_at: newSettings.skill_level_updated_at || null,
                        skill_assessment_completed: newSettings.skill_assessment_completed || false
                    })
                    .eq('id', user.id);

                if (profileError) {
                    console.error('Error syncing profile:', profileError);
                }

                if (newProgress) {
                    const { error } = await supabase
                        .from('user_progress')
                        .upsert({ user_id: user.id, progress: newProgress, updated_at: new Date() });
                    if (error) throw error;
                }

                setLastSynced(new Date());
                console.log('Saved to cloud');
            }
        } catch (err) {
            console.error('Error saving to cloud:', err);
        }
    }, [user]);

    // Save activity log
    const saveActivity = useCallback(async (activityData) => {
        if (!user) return;

        try {
            // Log user action for debugging context
            logBreadcrumb('Saving activity', 'data', {
                game_mode: activityData.game_mode,
                wpm: activityData.wpm,
                duration: activityData.duration_seconds
            });

            // Ensure started_at is present (DB requirement)
            if (!activityData.started_at) {
                // If not provided, assume the session just ended, so subtract duration
                const dur = activityData.duration_seconds || 0;
                const start = new Date(Date.now() - (dur * 1000));
                activityData.started_at = start;
            }

            // Ensure ended_at is present (DB requirement)
            if (!activityData.ended_at) {
                activityData.ended_at = new Date();
            }

            // Detect platform from user agent
            const detectPlatform = () => {
                const ua = navigator.userAgent || '';
                const uaLower = ua.toLowerCase();

                // iOS detection (iPhone, iPad, iPod)
                if (/iphone|ipad|ipod/.test(uaLower) ||
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
                    return 'ios';
                }

                // Android detection
                if (/android/.test(uaLower)) {
                    return 'android';
                }

                // Tablet detection (non-iOS/Android tablets)
                if (/tablet|playbook|silk/.test(uaLower)) {
                    return 'tablet';
                }

                // Desktop (default)
                return 'desktop';
            };

            const payload = {
                user_id: user.id,
                created_at: new Date(),
                started_at: activityData.started_at,
                ended_at: activityData.ended_at,
                mode: activityData.mode || activityData.game_mode, // Unified mode column
                platform: detectPlatform(),
                user_agent: navigator.userAgent,
                ...activityData
            };
            const { error } = await supabase
                .from('activities')
                .insert(payload);

            if (error) throw error;
            console.log('Activity saved to cloud');

            // Increment session counter and check Century Club achievement
            if (setUserSettings && userSettings) {
                const newSessionCount = (userSettings.totalSessions || 0) + 1;
                setUserSettings(prev => ({
                    ...prev,
                    totalSessions: newSessionCount
                }));

                // Check Century Club achievement (100 sessions)
                if (checkAchievementProgress && newSessionCount >= 100) {
                    checkAchievementProgress('session_milestone', { count: newSessionCount });
                }
            }
        } catch (err) {
            console.error('Error saving activity:', err);

            // Log to Sentry with context
            logError(err, {
                function: 'saveActivity',
                game_mode: activityData.game_mode,
                wpm: activityData.wpm,
                duration_seconds: activityData.duration_seconds,
                user_id: user.id
            });
        }
    }, [user, setUserSettings, userSettings, checkAchievementProgress]);

    // Save persistent QSO log
    const saveQSOLog = useCallback(async (logData) => {
        if (!user) return;
        try {
            const { error } = await supabase
                .from('qso_logs')
                .insert({
                    user_id: user.id,
                    contact_callsign: logData.call,
                    contact_name: logData.name,
                    contact_qth: logData.qth,
                    rst_rcvd: logData.rst,
                    rst_sent: logData.rstSent,
                    frequency: logData.frequency || '7.030',
                    mode: 'CW',
                    wpm: logData.wpm,
                    contact_type: logData.contact_type || 'system',
                    score: logData.score,
                    notes: logData.notes
                });

            if (error) throw error;
            console.log('QSO Log saved to cloud');
        } catch (err) {
            console.error('Error saving QSO log:', err);
        }
    }, [user]);

    // Auto-save when data changes (Debounced)
    useEffect(() => {
        if (!user) return;

        const timer = setTimeout(() => {
            saveToCloud(userSettings, progress);
        }, 2000); // 2 second debounce

        return () => clearTimeout(timer);
    }, [user, userSettings, progress, saveToCloud]);

    // Save adventure progress
    const saveAdventureProgress = useCallback(async (campaignId, saveData) => {
        if (!user) return;
        try {
            const { error } = await supabase
                .from('adventure_saves')
                .upsert({
                    user_id: user.id,
                    campaign_id: campaignId,
                    save_data: saveData,
                    updated_at: new Date()
                }, { onConflict: 'user_id, campaign_id' });

            if (error) throw error;
            console.log(`Adventure progress (${campaignId}) saved to cloud`);
        } catch (err) {
            console.error('Error saving adventure progress:', err);
        }
    }, [user]);

    // Load adventure progress
    const loadAdventureProgress = useCallback(async (campaignId) => {
        if (!user) return null;
        try {
            const { data, error } = await supabase
                .from('adventure_saves')
                .select('save_data')
                .eq('user_id', user.id)
                .eq('campaign_id', campaignId)
                .maybeSingle(); // Use maybeSingle to avoid 406/PGRST116 error on not found

            if (error) throw error;
            return data?.save_data || null;
        } catch (err) {
            console.error('Error loading adventure progress:', err);
            return null;
        }
    }, [user]);

    // Save literature progress
    const saveLiteratureProgress = useCallback(async (literatureId, status, completedSegments, bestWpm = null, bestAccuracy = null) => {
        if (!user) return;
        try {
            const payload = {
                user_id: user.id,
                literature_id: literatureId,
                status: status,
                completed_segments: completedSegments,
                last_accessed: new Date()
            };

            if (bestWpm !== null) payload.best_wpm = bestWpm;
            if (bestAccuracy !== null) payload.best_accuracy = bestAccuracy;

            const { error } = await supabase
                .from('literature_progress')
                .upsert(payload, { onConflict: 'user_id, literature_id' });

            if (error) throw error;
            console.log(`Literature progress (${literatureId}) saved to cloud: ${status}`);
        } catch (err) {
            console.error('Error saving literature progress:', err);
        }
    }, [user]);

    // Load all literature progress
    const loadAllLiteratureProgress = useCallback(async () => {
        if (!user) return null;
        try {
            const { data, error } = await supabase
                .from('literature_progress')
                .select('*')
                .eq('user_id', user.id);

            if (error) throw error;
            return data || [];
        } catch (err) {
            console.error('Error loading all literature progress:', err);
            return [];
        }
    }, [user]);

    // Save achievement
    const saveAchievement = useCallback(async (achievementId, metadata = {}) => {
        if (!user) return;
        try {
            const { error } = await supabase
                .from('user_achievements')
                .upsert({
                    user_id: user.id,
                    achievement_id: achievementId,
                    metadata: metadata,
                    unlocked_at: new Date()
                }, { onConflict: 'user_id, achievement_id' });

            if (error) throw error;
            console.log(`Achievement (${achievementId}) saved to cloud`);
        } catch (err) {
            console.error('Error saving achievement:', err);
        }
    }, [user]);

    // Save trivia unlock
    const saveTriviaUnlock = useCallback(async (triviaId) => {
        if (!user) return;
        try {
            const { error } = await supabase
                .from('user_trivia_unlocks')
                .upsert({
                    user_id: user.id,
                    trivia_id: triviaId,
                    unlocked_at: new Date()
                }, { onConflict: 'user_id, trivia_id' });

            if (error) throw error;
            console.log(`Trivia (${triviaId}) saved to cloud`);
        } catch (err) {
            console.error('Error saving trivia unlock:', err);
        }
    }, [user]);

    // Reset all user data in cloud
    const resetUserData = useCallback(async () => {
        if (!user) return;
        try {
            setIsSyncing(true);

            // 1. Delete auxiliary data
            await supabase.from('activities').delete().eq('user_id', user.id);
            await supabase.from('user_achievements').delete().eq('user_id', user.id);
            await supabase.from('user_trivia_unlocks').delete().eq('user_id', user.id);
            await supabase.from('literature_progress').delete().eq('user_id', user.id);
            await supabase.from('adventure_saves').delete().eq('user_id', user.id);

            // 2. Reset profiles data (identity remains, stats wiped)
            // We keep username/country as those are often desired to be kept, or we can wipe them too.
            // The local reset wipes them, so we should wipe them here to match.
            await supabase.from('profiles').update({
                streak: 0,
                max_wpm: 0,
                lifetime_score: 0,
                achievements: [],
                unlocked_trivia: [],
                skill_level: null,
                skill_level_updated_at: null,
                skill_assessment_completed: false,
                call_sign: '',
                // We leave username/country/region intact in profiles? 
                // If local state wipes callSign, we should wipe it in profile too.
                // Leaving username as it might be their display handle for auth.
            }).eq('id', user.id);

            // 3. Delete main settings/progress rows (forcing clean slate on next load)
            await supabase.from('user_settings').delete().eq('user_id', user.id);
            await supabase.from('user_progress').delete().eq('user_id', user.id);

            console.log('Cloud data reset complete');
            setLastSynced(new Date());
        } catch (err) {
            console.error('Error resetting cloud data:', err);
            throw err; // Re-throw to alert user
        } finally {
            setIsSyncing(false);
        }
    }, [user]);

    return {
        isSyncing,
        lastSynced,
        saveToCloud,
        resetUserData,
        saveActivity,
        saveAdventureProgress,
        loadAdventureProgress,
        saveLiteratureProgress,
        loadAllLiteratureProgress,
        saveAchievement,
        saveTriviaUnlock,
        saveQSOLog
    };
};
